/**
 * Secure in-memory cache for private keys, certificates, and thumbprints
 * Keys, certificates, and thumbprints are loaded once and kept in memory during session
 * Cleared on app close or explicit clear
 * All environments (dev, essai, prod) can be cached simultaneously
 */
import mevCertService from './mevCertificateService';
import { readThumbprintPointer, writeThumbprintPointer } from './thumbprintPointer';

class CertificateMemoryCache {
  private keyCache: Map<string, string> = new Map(); // key: `${storeId}_${env}`, value: privateKeyPem
  private certCache: Map<string, string> = new Map(); // key: `${storeId}_${env}`, value: certificatePem
  private thumbprintCache: Map<string, string> = new Map(); // key: `${storeId}_${env}`, value: thumbprint
  private psiCertCache: Map<string, string> = new Map(); // key: `${storeId}_${env}`, value: PSI certificate PEM
  private psiThumbprintCache: Map<string, string> = new Map(); // key: `${storeId}_${env}`, value: PSI thumbprint
  private loaded: Set<string> = new Set(); // Track which keys/certs have been loaded

  /**
   * Internal method to ensure memory cache is in sync with global localStorage pointer.
   * Checks for environment-wide certificate updates from other tabs.
   * Returns true if a reload was performed.
   */
  private async ensureSynced(storeId: string, env: string): Promise<boolean> {
    const cacheKey = `${storeId}_${env}`;
    const currentGlobalThumbprint = readThumbprintPointer(storeId, env);
    const cachedThumbprint = this.thumbprintCache.get(cacheKey);

    // If not loaded yet OR mismatches the global pointer, trigger a reload
    if (!this.loaded.has(cacheKey) || (currentGlobalThumbprint && currentGlobalThumbprint !== cachedThumbprint)) {
      console.log(`[CertificateMemoryCache] Sync Check: ${!this.loaded.has(cacheKey) ? 'Not loaded' : 'Mismatch detected'}. Global: ${currentGlobalThumbprint}, Cached: ${cachedThumbprint}. Initializing reload for ${cacheKey}...`);
      await this.loadPrivateKey(storeId, env);
      return true;
    }

    return false;
  }

  /**
   * Load private key and certificate into memory cache
   * Called on-demand when first needed or when sync mismatch is detected
   */
  async loadPrivateKey(storeId: string, env: string): Promise<void> {
    const cacheKey = `${storeId}_${env}`;

    try {
      console.log(`[CertificateMemoryCache] ========================================`);
      console.log(`[CertificateMemoryCache] LOAD REQUEST for ${storeId}/${env}`);
      console.log(`[CertificateMemoryCache] Current cached thumbprint: ${this.thumbprintCache.get(cacheKey) || 'NONE'}`);
      console.log(`[CertificateMemoryCache] localStorage pointer: ${readThumbprintPointer(storeId, env) || 'NONE'}`);
      console.log(`[CertificateMemoryCache] Calling getActiveCertThumbprint to select certificate...`);
      console.log(`[CertificateMemoryCache] ========================================`);

      // Get fresh active thumbprint (this selects the best valid cert)
      const thumbprint = await mevCertService.getActiveCertThumbprint(storeId, env);
      if (!thumbprint) {
        throw new Error(`No certificate found for ${storeId}/${env}`);
      }

      console.log(`[CertificateMemoryCache] ========================================`);
      console.log(`[CertificateMemoryCache] getActiveCertThumbprint RETURNED: ${thumbprint}`);
      console.log(`[CertificateMemoryCache] ========================================`);

      console.log(`[CertificateMemoryCache] Loading certificate with thumbprint: ${thumbprint}`);

      // Retrieve both from Windows Certificate Store
      const result = await (window as any).api.invoke('get-certificate-and-key-from-windows-store', thumbprint);
      if (!result.success || !result.privateKeyPem || !result.certPem) {
        throw new Error(`Failed to retrieve certificate and key: ${result.error}`);
      }

      // Store in memory cache
      this.keyCache.set(cacheKey, result.privateKeyPem);
      this.certCache.set(cacheKey, result.certPem);
      this.thumbprintCache.set(cacheKey, thumbprint);

      // Also load PSI certificate
      try {
        const psiResult = await mevCertService.getPSICertificateThumbprint(storeId, env);
        if (psiResult.thumbprint) {
          const psiCertResult = await (window as any).api.invoke('get-public-certificate-from-windows-store', psiResult.thumbprint);
          if (psiCertResult.success && psiCertResult.certPem) {
            this.psiCertCache.set(cacheKey, psiCertResult.certPem);
            this.psiThumbprintCache.set(cacheKey, psiResult.thumbprint);
            console.log(`[CertificateMemoryCache] ✓ Cached PSI certificate for ${cacheKey}`);
          }
        }
      } catch (psiError) {
        console.warn(`[CertificateMemoryCache] PSI load skipped:`, psiError);
      }

      this.loaded.add(cacheKey);

      writeThumbprintPointer(storeId, env, thumbprint);

      console.log(`[CertificateMemoryCache] ✓ Cache refreshed for ${cacheKey} (${thumbprint})`);
    } catch (error) {
      console.error(`[CertificateMemoryCache] Error loading for ${cacheKey}:`, error);
      throw error;
    }
  }

  /**
   * Get private key from memory cache
   * Returns null if not available
   */
  async getPrivateKey(storeId: string, env: string): Promise<string | null> {
    await this.ensureSynced(storeId, env);
    const cacheKey = `${storeId}_${env}`;
    return this.keyCache.get(cacheKey) || null;
  }

  /**
   * Get certificate from memory cache
   * Returns null if not available
   */
  async getCertificate(storeId: string, env: string): Promise<string | null> {
    await this.ensureSynced(storeId, env);
    const cacheKey = `${storeId}_${env}`;
    return this.certCache.get(cacheKey) || null;
  }

  /**
   * Get thumbprint from memory cache
   * Returns null if not available
   */
  async getThumbprint(storeId: string, env: string): Promise<string | null> {
    await this.ensureSynced(storeId, env);
    const cacheKey = `${storeId}_${env}`;
    return this.thumbprintCache.get(cacheKey) || null;
  }

  /**
   * Get PSI certificate from memory cache
   */
  async getCertifPSI(storeId: string, env: string): Promise<string | null> {
    await this.ensureSynced(storeId, env);
    const cacheKey = `${storeId}_${env}`;
    return this.psiCertCache.get(cacheKey) || null;
  }

  /**
   * Get PSI thumbprint from memory cache
   */
  async getPSIThumbprint(storeId: string, env: string): Promise<string | null> {
    await this.ensureSynced(storeId, env);
    const cacheKey = `${storeId}_${env}`;
    return this.psiThumbprintCache.get(cacheKey) || null;
  }

  /**
   * Force reload PSI certificate
   */
  async reloadPSICertificate(storeId: string, env: string): Promise<void> {
    const cacheKey = `${storeId}_${env}`;
    console.log(`[CertificateMemoryCache] Reloading PSI for ${cacheKey}...`);
    const psiResult = await mevCertService.getPSICertificateThumbprint(storeId, env);
    if (psiResult.thumbprint) {
      const psiCertResult = await (window as any).api.invoke('get-public-certificate-from-windows-store', psiResult.thumbprint);
      if (psiCertResult.success && psiCertResult.certPem) {
        this.psiCertCache.set(cacheKey, psiCertResult.certPem);
        this.psiThumbprintCache.set(cacheKey, psiResult.thumbprint);
      }
    }
  }

  /**
   * Clear cache for specific store/env
   */
  clearPrivateKey(storeId: string, env: string): void {
    const cacheKey = `${storeId}_${env}`;
    this.keyCache.delete(cacheKey);
    this.certCache.delete(cacheKey);
    this.thumbprintCache.delete(cacheKey);
    this.psiCertCache.delete(cacheKey);
    this.psiThumbprintCache.delete(cacheKey);
    this.loaded.delete(cacheKey);
    writeThumbprintPointer(storeId, env, "");
  }

  /**
   * Clear all cached data
   */
  clearAll(): void {
    this.keyCache.clear();
    this.certCache.clear();
    this.thumbprintCache.clear();
    this.psiCertCache.clear();
    this.psiThumbprintCache.clear();
    this.loaded.clear();
  }

  /**
   * Sync check for hooks or UI
   */
  isLoaded(storeId: string, env: string): boolean {
    const cacheKey = `${storeId}_${env}`;
    return this.loaded.has(cacheKey);
  }

  /**
   * Explicit load by thumbprint (bypasses selection logic)
   */
  async loadPrivateKeyByThumbprint(storeId: string, env: string, thumbprint: string): Promise<void> {
    const cacheKey = `${storeId}_${env}`;

    console.log(`[CertificateMemoryCache] Loading by explicit thumbprint: ${thumbprint}`);

    // Clear existing cache
    this.keyCache.delete(cacheKey);
    this.certCache.delete(cacheKey);
    this.thumbprintCache.delete(cacheKey);
    this.psiCertCache.delete(cacheKey);
    this.psiThumbprintCache.delete(cacheKey);
    this.loaded.delete(cacheKey);

    // Load main certificate and private key
    const result = await (window as any).api.invoke('get-certificate-and-key-from-windows-store', thumbprint);
    if (result.success && result.privateKeyPem && result.certPem) {
      this.keyCache.set(cacheKey, result.privateKeyPem);
      this.certCache.set(cacheKey, result.certPem);
      this.thumbprintCache.set(cacheKey, thumbprint);

      // CRITICAL: Also load PSI certificate (same as loadPrivateKey)
      try {
        const psiResult = await mevCertService.getPSICertificateThumbprint(storeId, env);
        if (psiResult.thumbprint) {
          const psiCertResult = await (window as any).api.invoke('get-public-certificate-from-windows-store', psiResult.thumbprint);
          if (psiCertResult.success && psiCertResult.certPem) {
            this.psiCertCache.set(cacheKey, psiCertResult.certPem);
            this.psiThumbprintCache.set(cacheKey, psiResult.thumbprint);
            console.log(`[CertificateMemoryCache] ✓ Cached PSI certificate for ${cacheKey}`);
          }
        }
      } catch (psiError) {
        console.warn(`[CertificateMemoryCache] PSI load skipped:`, psiError);
      }

      this.loaded.add(cacheKey);

      writeThumbprintPointer(storeId, env, thumbprint);

      console.log(`[CertificateMemoryCache] ✓ Loaded by explicit thumbprint: ${thumbprint}`);
    } else {
      throw new Error(`Failed to load certificate by thumbprint: ${result.error}`);
    }
  }
}

const certificateMemoryCache = new CertificateMemoryCache();
export default certificateMemoryCache;
